import React, {useMemo} from 'react';

import {EDragData} from 'webapp-lib/typings';

import {ETable} from './ETable';
import {
  ETableCell,
  ETableCellAlignment,
  ETableCellObject,
  ETableCellValue,
  ETableColumn,
  ETableMultiAction,
  ETableProps,
  ETableRow,
  ETableSingleAction,
  StyleType,
} from './types';

// code moved from webapp, disable conflicting rules in webapp-lib for now.
/* eslint-disable @typescript-eslint/no-explicit-any */

export type SimpleTableColumn<ItemType extends object, Sort = string> = Pick<
  ETableColumn<string, Sort>,
  | 'name'
  | 'label'
  | 'labelDescriptor'
  | 'tooltipTitle'
  | 'sortName'
  | 'width'
  | 'defaultHidden'
> & {
  /** Callback that returns a cell value, or `null` to make it merge with previous cell (i.e. colspan). */
  cell: ((item: ItemType) => ETableCellValue) | null;
  /** Text alignment of both the table header and body for this specific column. */
  align?: ETableCellAlignment;
  textSelectable?: boolean;
} & Pick<ETableCellObject, 'mode'>;

/**
 * Enforces a `dataId` callback when `ItemType` does not have an `id` property.
 *
 * Note that the braces before and after `extends` are necessary. Without them,
 * the type of `ItemType` would be inferred as `object` instead of the passed type.
 * https://stackoverflow.com/questions/65492464/typescript-never-type-condition
 */
type OptionalDataId<ItemType> = [ItemType] extends [{id: string}]
  ? {dataId?: never}
  : {
      /**
       * When your data does not have an `id` prop (but `casusId`, for example), you *must* specify the ID
       * callback manually. Otherwise, you might run into problems when sorting, adding, or removing items.
       */
      dataId: (item: ItemType, index: number) => string;
    };

type Action<ItemType, ActionType> =
  | ActionType[]
  | ((item: ItemType) => ActionType[]);

type Props<ItemType extends object, Sort extends string> = {
  columns: SimpleTableColumn<ItemType, Sort>[];
  data: ItemType[];
  /**
   * Actions that can be applied on a single row. Either an array or a function that returns an array.
   */
  singleActions?: Action<ItemType, ETableSingleAction>;
  /**
   * Actions that can be applied on multiple selected rows. Either an array or a function that returns an array.
   */
  multiActions?: Action<ItemType, ETableMultiAction>;
  /**
   * StyleType that can be applied to the row. Either an allowed string or a function returning an allowed string
   */
  styleType?: StyleType | ((item: ItemType) => StyleType | undefined);
  /** Flag indicating whether all rows of the table have a select box. */
  isSelectable?: boolean;
  /** Flag indicating whether all rows of the table are prevented of being activated. */
  notActivatable?: boolean;
  /** Flag indicating that there should be no hover effect */
  noHoverEffect?: boolean;
  /** DragDataList options that get passed as drag data on drag event. Can be function or list. */
  dragDataList?: EDragData[] | ((item: ItemType) => EDragData[]);
  /** used to persist settings for this table, leaving it out not persist thigns like columns order and visibility */
  persistenceId?: string;
} & OptionalDataId<ItemType> &
  Pick<
    ETableProps<string, Sort>,
    | 'order'
    | 'onOrderChange'
    | 'activeItem'
    | 'onActiveItemChange'
    | 'selectedItems'
    | 'onSelectedItemsChange'
    | 'hasMoreBefore'
    | 'hasMoreAfter'
    | 'onLoadMoreBefore'
    | 'onLoadMoreAfter'
    | 'placeholder'
    | 'scrollIntoView'
    | 'testId'
    | 'hideHeader'
    | 'refreshIndicator'
  >;

const executeIfFunction = <T,>(
  maybeFunction: T,
  arg: any
): T extends (arg: any) => any ? ReturnType<T> : T =>
  typeof maybeFunction === 'function' ? maybeFunction(arg) : maybeFunction;

/**
 * Abstraction of the `ETable` component to make simple tables simple.
 *
 * Either all or no rows have a checkbox and/or extra actions. Expansion is not supported.
 * If you need more features, use the full `ETable` component.
 */
export const SimpleTable = <ItemType extends object, Sort extends string>({
  columns: userColumns,
  data: userData,
  singleActions,
  multiActions,
  notActivatable = false,
  noHoverEffect = false,
  isSelectable = false,
  dataId,
  dragDataList,
  styleType,
  ...props
}: Props<ItemType, Sort>): React.ReactElement => {
  const columns = useMemo(
    (): ETableColumn<string, Sort>[] =>
      userColumns.map(
        ({
          name,
          label,
          labelDescriptor,
          tooltipTitle,
          sortName,
          align,
          width,
          textSelectable,
          defaultHidden,
        }) => ({
          label,
          labelDescriptor,
          tooltipTitle,
          name,
          sortName,
          align,
          width,
          textSelectable,
          defaultHidden,
        })
      ),
    [userColumns]
  );

  const interactionRestrictionLevel = getInteractionRestrictionLevel(
    noHoverEffect,
    notActivatable
  );

  const data = useMemo(
    (): ETableRow[] =>
      userData.map((item, rowIndex) => ({
        id: dataId?.(item, rowIndex) || (item as any).id,
        ...userColumns.reduce<Record<string, ETableCell>>((cols, col) => {
          cols[col.name] = col.cell
            ? {
                cell: col.cell(item),
                align: col.align,
                mode: col.mode,
                textSelectable: col.textSelectable,
              }
            : col.cell;
          return cols;
        }, {}),
        raw: item,
        options: {
          singleActions: executeIfFunction(singleActions, item),
          multiActions: executeIfFunction(multiActions, item),
          styleType: executeIfFunction(styleType, item),
          isSelectable,
          interactionRestrictionLevel,
          dragDataList: executeIfFunction(dragDataList, item),
        },
      })),
    [
      userData,
      dataId,
      userColumns,
      singleActions,
      multiActions,
      styleType,
      isSelectable,
      interactionRestrictionLevel,
      dragDataList,
    ]
  );
  return <ETable columns={columns} data={data} {...props} />;
};

const getInteractionRestrictionLevel = (
  noHoverEffect: boolean | undefined,
  notActivatable: boolean | undefined
): 'noHoverEffect' | 'notActivatable' | undefined => {
  if (noHoverEffect) {
    return 'noHoverEffect';
  } else if (notActivatable) {
    return 'notActivatable';
  } else {
    return undefined;
  }
};
